import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { GripVertical, Save, X, CheckCircle, AlertCircle } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { toast } from 'react-hot-toast';

/**
 * TaskReorderAdmin Component
 *
 * Allows admins to manually reorder tasks by dragging and dropping
 * Updates the sequence_order field for each task
 */
const TaskReorderAdmin = ({ userId, onClose }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draggedTaskId, setDraggedTaskId] = useState(null);
  const [dragOverTaskId, setDragOverTaskId] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Load tasks for the user
  useEffect(() => {
    if (userId) {
      loadTasks();
    }
  }, [userId]);

  const loadTasks = async () => {
    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('timeline_items')
        .select('id, title, category, scheduled_date, sequence_order, status, workflow_phase, college_name, metadata')
        .eq('user_id', userId);

      if (error) throw error;

      // Sort by sequence_order if it exists, otherwise by scheduled_date
      const sorted = (data || []).sort((a, b) => {
        // If both have sequence_order, use that
        if (a.sequence_order != null && b.sequence_order != null) {
          return a.sequence_order - b.sequence_order;
        }
        // If only one has sequence_order, that comes first
        if (a.sequence_order != null) return -1;
        if (b.sequence_order != null) return 1;
        // Otherwise sort by date
        const dateA = new Date(a.scheduled_date || '9999-12-31');
        const dateB = new Date(b.scheduled_date || '9999-12-31');
        return dateA - dateB;
      });

      // Assign sequence_order to all tasks based on current sort position
      const tasksWithOrder = sorted.map((task, index) => ({
        ...task,
        sequence_order: task.sequence_order ?? index + 1,
        originalOrder: task.sequence_order ?? index + 1
      }));

      setTasks(tasksWithOrder);
      setLoading(false);
    } catch (error) {
      console.error('Error loading tasks:', error);
      toast.error('Failed to load tasks');
      setLoading(false);
    }
  };

  // Handle drag start
  const handleDragStart = (e, taskId) => {
    setDraggedTaskId(taskId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target);
    e.target.style.opacity = '0.4';
  };

  // Handle drag over
  const handleDragOver = (e, taskId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    if (draggedTaskId !== taskId) {
      setDragOverTaskId(taskId);
    }
  };

  // Handle drag end
  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedTaskId(null);
    setDragOverTaskId(null);
  };

  // Handle drop
  const handleDrop = (e, targetTaskId) => {
    e.preventDefault();
    e.stopPropagation();

    if (draggedTaskId === targetTaskId) return;

    // Reorder tasks
    const draggedIndex = tasks.findIndex(t => t.id === draggedTaskId);
    const targetIndex = tasks.findIndex(t => t.id === targetTaskId);

    if (draggedIndex === -1 || targetIndex === -1) return;

    const newTasks = [...tasks];
    const [draggedTask] = newTasks.splice(draggedIndex, 1);
    newTasks.splice(targetIndex, 0, draggedTask);

    // Reassign sequence_order
    const reorderedTasks = newTasks.map((task, index) => ({
      ...task,
      sequence_order: index + 1
    }));

    setTasks(reorderedTasks);
    setHasChanges(true);
    setDraggedTaskId(null);
    setDragOverTaskId(null);
  };

  // Save order to database
  const handleSave = async () => {
    try {
      setSaving(true);

      // Only update tasks where sequence_order changed
      const tasksToUpdate = tasks.filter(task =>
        task.sequence_order !== task.originalOrder
      );

      if (tasksToUpdate.length === 0) {
        toast.success('No changes to save');
        setSaving(false);
        return;
      }

      // Update each task
      const updates = tasksToUpdate.map(task =>
        supabase
          .from('timeline_items')
          .update({
            sequence_order: task.sequence_order,
            updated_at: new Date().toISOString()
          })
          .eq('id', task.id)
      );

      const results = await Promise.all(updates);

      const errors = results.filter(r => r.error);
      if (errors.length > 0) {
        console.error('Errors updating tasks:', errors);
        throw new Error('Failed to update some tasks');
      }

      toast.success(`Successfully reordered ${tasksToUpdate.length} tasks`);
      setHasChanges(false);

      // Refresh tasks to get updated data
      loadTasks();

    } catch (error) {
      console.error('Error saving task order:', error);
      toast.error('Failed to save task order');
    } finally {
      setSaving(false);
    }
  };

  // Reset to original order
  const handleReset = () => {
    const resetTasks = tasks.map(task => ({
      ...task,
      sequence_order: task.originalOrder
    })).sort((a, b) => a.originalOrder - b.originalOrder);

    setTasks(resetTasks);
    setHasChanges(false);
  };

  // Get category color
  const getCategoryColor = (category) => {
    const colors = {
      'application': 'bg-azure-100 text-azure-700',
      'essay': 'bg-violet-100 text-violet-700',
      'testing': 'bg-orange-100 text-orange-700',
      'financial_aid': 'bg-emerald-100 text-emerald-700',
      'profile': 'bg-indigo-100 text-indigo-700',
      'research': 'bg-teal-100 text-teal-700'
    };
    return colors[category] || 'bg-slate-100 text-slate-700';
  };

  // Get status icon
  const getStatusIcon = (task) => {
    if (task.workflow_phase === 'complete' || task.status === 'completed') {
      return <CheckCircle className="w-4 h-4 text-emerald-500" />;
    }
    return <AlertCircle className="w-4 h-4 text-slate-400" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-azure-500 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading tasks...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Reorder Tasks</h2>
          <p className="text-sm text-slate-600 mt-1">
            Drag and drop tasks to manually set their order
          </p>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Instructions */}
      <div className="px-6 py-3 bg-azure-50 border-b border-azure-100">
        <p className="text-sm text-azure-800">
          💡 <strong>Tip:</strong> Drag tasks to reorder them. ED tasks are marked with 🔴. Use filters to manage large task lists.
        </p>
      </div>

      {/* Filters */}
      <div className="px-6 py-3 border-b border-slate-200 bg-slate-50">
        <div className="flex gap-3 items-center">
          <input
            type="text"
            placeholder="Search tasks..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-azure-500"
          />
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-azure-500"
          >
            <option value="all">All Categories</option>
            <option value="application">Application</option>
            <option value="essay">Essay</option>
            <option value="testing">Testing</option>
            <option value="financial_aid">Financial Aid</option>
            <option value="profile">Profile</option>
            <option value="research">Research</option>
          </select>
        </div>
      </div>

      {/* Task List */}
      <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
        {(() => {
          // Apply filters
          const filteredTasks = tasks.filter(task => {
            const matchesCategory = categoryFilter === 'all' || task.category === categoryFilter;
            const matchesSearch = searchTerm === '' ||
              task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
              task.college_name?.toLowerCase().includes(searchTerm.toLowerCase());
            return matchesCategory && matchesSearch;
          });

          if (filteredTasks.length === 0) {
            return (
              <div className="text-center py-8">
                <p className="text-slate-600">
                  {tasks.length === 0 ? 'No tasks found for this student' : 'No tasks match your filters'}
                </p>
              </div>
            );
          }

          return (
            <div className="space-y-1.5">
              <div className="text-xs text-slate-500 mb-2">
                Showing {filteredTasks.length} of {tasks.length} tasks
              </div>
              {filteredTasks.map((task, index) => (
              <div
                key={task.id}
                draggable
                onDragStart={(e) => handleDragStart(e, task.id)}
                onDragOver={(e) => handleDragOver(e, task.id)}
                onDragEnd={handleDragEnd}
                onDrop={(e) => handleDrop(e, task.id)}
                className={`
                  flex items-center gap-2 p-2 rounded-lg border-2 transition-all cursor-move
                  ${draggedTaskId === task.id ? 'opacity-40' : 'opacity-100'}
                  ${dragOverTaskId === task.id ? 'border-azure-400 bg-azure-50' : 'border-slate-200 bg-white hover:bg-slate-50'}
                `}
              >
                {/* Drag Handle */}
                <GripVertical className="w-4 h-4 text-slate-400 flex-shrink-0" />

                {/* Order Number */}
                <div className="flex-shrink-0 w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center">
                  <span className="text-xs font-semibold text-slate-700">{task.sequence_order}</span>
                </div>

                {/* Task Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    {getStatusIcon(task)}
                    {/* ED Indicator */}
                    {task.metadata?.deadline_type === 'ed' && (
                      <span className="text-red-500 text-xs font-bold" title="Early Decision">🔴 ED</span>
                    )}
                    <h3 className="text-xs font-medium text-slate-900 truncate">
                      {task.title}
                    </h3>
                  </div>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${getCategoryColor(task.category)}`}>
                      {task.category}
                    </span>
                    {task.college_name && (
                      <span className="text-xs text-slate-600 font-medium truncate">
                        {task.college_name}
                      </span>
                    )}
                    {task.scheduled_date && (
                      <span className="text-xs text-slate-500">
                        {format(parseISO(task.scheduled_date), 'MMM dd')}
                      </span>
                    )}
                  </div>
                </div>

                {/* Change Indicator */}
                {task.sequence_order !== task.originalOrder && (
                  <div className="flex-shrink-0 px-1.5 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded">
                    Moved
                  </div>
                )}
              </div>
            ))}
            </div>
          );
        })()}
      </div>

      {/* Footer Actions */}
      <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 bg-slate-50">
        <div className="text-sm text-slate-600">
          {hasChanges ? (
            <span className="text-amber-600 font-medium">
              Unsaved changes - {tasks.filter(t => t.sequence_order !== t.originalOrder).length} tasks reordered
            </span>
          ) : (
            <span>All changes saved</span>
          )}
        </div>

        <div className="flex items-center gap-3">
          {hasChanges && (
            <button
              onClick={handleReset}
              className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
              disabled={saving}
            >
              Reset
            </button>
          )}

          <button
            onClick={handleSave}
            disabled={!hasChanges || saving}
            className="px-4 py-2 text-sm font-medium text-white bg-azure-500 rounded-lg hover:bg-azure-600 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                <span>Save Order</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TaskReorderAdmin;
